import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faChevronUp,
  faTrash,
  faAdd,
  faEdit,
  faSpinner,
  faArrowDown,
  faCalendar,
  faMusic
} from '@fortawesome/free-solid-svg-icons';
import TourContext from '../context/TourContext';
import UserContext from '../context/UserContext';
import { 
  getFirestoreInstance 
} from '../utils/firebaseHelper';
import { 
  collection, 
  query, 
  getCountFromServer 
} from 'firebase/firestore';
import { normalizeDate } from '../utils/dateUtils';
import styles from './RightMenu.module.css';

const RightMenu = () => {
  console.log("RightMenu component rendering");
  
  const { 
    selectedEvent, 
    setSelectedEvent, 
    events,
    setEvents,
    selectedDate,
    setSelectedDate,
    fetchEventsData,
    loadMoreEvents,
    hasMoreEvents,
    updateEventData,
    isLoading,
    deleteEvent
  } = useContext(TourContext);
  
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filteredDates, setFilteredDates] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingDates, setIsLoadingDates] = useState(true);
  const [filterType, setFilterType] = useState('all'); // 'all', 'tour', or 'show'
  const lastSelectedEventRef = useRef(null); // Keep track of the last selected event
  const filteringInProgressRef = useRef(false); // Prevent concurrent filtering

  // Log whenever significant props/state changes
  useEffect(() => {
    console.log("=== RightMenu Component State Update ===");
    console.log("userData:", userData);
    console.log("selectedEvent:", selectedEvent);
    console.log("events count:", events?.length || 0);
    console.log("selectedDate:", selectedDate);
    console.log("filteredDates count:", filteredDates.length);
    console.log("filterType:", filterType);
    
    // If there's a selectedEvent, log its structure
    if (selectedEvent) {
      console.log("Selected Event Details:");
      console.log("- ID:", selectedEvent.id);
      console.log("- Type:", selectedEvent.type);
      console.log("- Name:", selectedEvent.tourName || selectedEvent.name);
      console.log("- Dates count:", selectedEvent.eventDates?.length || 0);
      
      // Log the first few dates
      if (selectedEvent.eventDates && selectedEvent.eventDates.length > 0) {
        console.log("- First 3 dates:", selectedEvent.eventDates.slice(0, 3).map(d => ({
          id: d.id,
          date: d.date
        })));
      }
    }
  }, [userData, selectedEvent, events?.length, selectedDate, filteredDates.length, filterType]);

  // Fetch initial events data
  useEffect(() => {
    console.log("Fetch initial events effect triggered");
    console.log("userData:", userData);
    console.log("isInitialLoad:", isInitialLoad);
    
    const fetchEvents = async () => {
      if (userData && isInitialLoad) {
        console.log("Fetching initial events data");
        await fetchEventsData(userData, 1);
        setIsInitialLoad(false);
        console.log("Initial events fetch completed");
      } else {
        console.log("Skipping initial events fetch");
      }
    };
    fetchEvents();
  }, [userData, fetchEventsData, isInitialLoad]);
  
  // Handle loading more events
  const handleLoadMore = async () => {
    console.log("Load more button clicked");
    console.log("isLoadingMore:", isLoadingMore);
    
    if (!userData || isLoadingMore) {
      console.log("Skipping load more - already loading or no user");
      return;
    }
    
    setIsLoadingMore(true);
    try {
      console.log("Loading more events");
      await loadMoreEvents(userData);
      console.log("Load more completed successfully");
    } catch (error) {
      console.error('Error loading more events:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Update selectedEvent if needed when events change
  useEffect(() => {
    console.log("Events dependency changed, checking if selectedEvent needs update");
    
    if (events && events.length > 0) {
      console.log("Events array has items, checking selectedEvent");
      
      // Apply filter to events based on filterType
      const filteredEvents = events.filter(event => 
        filterType === 'all' || (event && event.type === filterType)
      );
      
      // If there's a selectedEvent but it's not in the filtered events array
      if (selectedEvent) {
        console.log("Current selectedEvent:", selectedEvent.id, selectedEvent.type);
        const eventExists = filteredEvents.some(e => 
          e && e.id === selectedEvent.id && e.type === selectedEvent.type
        );
        console.log("Selected event exists in filtered events array?", eventExists);
        
        if (!eventExists) {
          console.log("Selected event not found in filtered events, updating to first event");
          const firstEvent = filteredEvents[0];
          if (firstEvent) {
            setSelectedEvent(firstEvent);
            
            // Auto-select the latest date for the first event (but don't navigate)
            if (firstEvent.eventDates && firstEvent.eventDates.length > 0) {
              const sortedDates = [...firstEvent.eventDates].sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateB - dateA; // Latest first
              });
              const latestDate = sortedDates[0];
              console.log("Auto-selecting latest date:", latestDate.id);
              setSelectedDate(latestDate.id);
            }
          }
        } else {
          // Update the selected event with fresh data
          const updatedEvent = filteredEvents.find(e => 
            e && e.id === selectedEvent.id && e.type === selectedEvent.type
          );
          if (updatedEvent && JSON.stringify(updatedEvent) !== JSON.stringify(selectedEvent)) {
            console.log("Updated event data available, updating selectedEvent");
            setSelectedEvent(updatedEvent);
          } else {
            console.log("No changes needed for selectedEvent");
          }
        }
      } else if (filteredEvents.length > 0) {
        console.log("No selectedEvent, setting to first filtered event");
        const firstEvent = filteredEvents[0];
        setSelectedEvent(firstEvent);
        
        // Auto-select the latest date for the first event (but don't navigate)
        if (firstEvent && firstEvent.eventDates && firstEvent.eventDates.length > 0) {
          const sortedDates = [...firstEvent.eventDates].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA; // Latest first
          });
          const latestDate = sortedDates[0];
          console.log("Auto-selecting latest date:", latestDate.id);
          setSelectedDate(latestDate.id);
        }
      }
    } else {
      console.log("No events available");
    }
  }, [events, selectedEvent, setSelectedEvent, setSelectedDate, filterType]);

  // Filter dates based on selectedEvent and userData
  useEffect(() => {
    console.log("=== FILTERING DATES ===");
    console.log("selectedEvent:", selectedEvent?.id, selectedEvent?.type);
    console.log("userData:", userData?.userId, userData?.userType);
    
    // Skip if filtering is already in progress or we don't have data
    if (filteringInProgressRef.current || !selectedEvent || !userData) {
      if (!selectedEvent || !userData) {
        console.log("No selectedEvent or userData, clearing filteredDates");
        setFilteredDates([]);
        setIsLoadingDates(false);
      }
      return;
    }
    
    // Set flag to prevent concurrent filtering
    filteringInProgressRef.current = true;
    setIsLoadingDates(true);
    
    // Check if this is a different event than last time to avoid redundant processing
    const isNewEvent = !lastSelectedEventRef.current || 
                       lastSelectedEventRef.current.id !== selectedEvent.id || 
                       lastSelectedEventRef.current.type !== selectedEvent.type;
    
    if (isNewEvent) {
      console.log("New event selected, updating lastSelectedEventRef");
      lastSelectedEventRef.current = {
        id: selectedEvent.id,
        type: selectedEvent.type
      };
    }
    
    // Find the current event in the events array to check user permissions
    const currentEventAssignment = events.find(e => 
      e.id === selectedEvent.id && e.type === selectedEvent.type
    );
    
    console.log("Current event assignment found:", !!currentEventAssignment);
    
    // Immediately run filtering logic without timeout
    try {
      console.log("Running date filtering logic");
      console.log("Selected event dates:", selectedEvent.eventDates?.length || 0);
      
      // Safety check for dates
      if (!selectedEvent.eventDates || selectedEvent.eventDates.length === 0) {
        console.log("No dates available");
        setFilteredDates([]);
        setIsLoadingDates(false);
        filteringInProgressRef.current = false;
        return;
      }
      
      // If user is the event creator, show all dates
      if (selectedEvent.createdBy && selectedEvent.createdBy.userId === userData.userId) {
        console.log("User is the event creator, showing all dates");
        setFilteredDates(selectedEvent.eventDates || []);
        setIsLoadingDates(false);
        filteringInProgressRef.current = false;
        return;
      }
      
      // For all events (both tours and shows), check assigned dates from the events array
      if (currentEventAssignment) {
        console.log("Checking assigned dates from event assignment");
        
        // For vendors, check if they're accepted for specific dates
        if (userData.userType === 'Vendor') {
          console.log("User is a vendor, filtering for accepted dates only");
          
          if (currentEventAssignment.data?.assignedDates && currentEventAssignment.data.assignedDates.length > 0) {
            // Filter to only dates with 'accepted' status
            const acceptedDateIds = currentEventAssignment.data.assignedDates
              .filter(date => date.status === 'accepted')
              .map(date => date.dateDocId || date.dateId);
            
            console.log("Accepted date IDs:", acceptedDateIds);
            
            if (acceptedDateIds.length === 0) {
              console.log("No accepted dates found for vendor");
              setFilteredDates([]);
              setIsLoadingDates(false);
              filteringInProgressRef.current = false;
              return;
            }
            
            // Filter dates to only include those with accepted status
            const filteredDates = selectedEvent.eventDates.filter(date =>
              acceptedDateIds.includes(date.id)
            );
            
            console.log("Filtered dates count for vendor:", filteredDates.length);
            setFilteredDates(filteredDates);
            setIsLoadingDates(false);
            filteringInProgressRef.current = false;
            return;
          } else {
            console.log("Vendor not assigned to any dates");
            setFilteredDates([]);
            setIsLoadingDates(false);
            filteringInProgressRef.current = false;
            return;
          }
        } else {
          // For all other user types, check assigned dates
          console.log(`User is ${userData.userType}, checking assigned dates`);
          
          if (currentEventAssignment.data?.assignedDates && currentEventAssignment.data.assignedDates.length > 0) {
            // Get the assigned date IDs
            const assignedDateIds = currentEventAssignment.data.assignedDates.map(date => 
              date.dateDocId || date.dateId
            );
            
            console.log("Assigned date IDs:", assignedDateIds);
            
            // For shows with only one date, if the user has any access, show the date
            if (selectedEvent.type === 'show' && selectedEvent.eventDates.length === 1) {
              console.log("Show with single date - user has access");
              setFilteredDates(selectedEvent.eventDates);
              setIsLoadingDates(false);
              filteringInProgressRef.current = false;
              return;
            }
            
            // Filter dates to only include those assigned to this user
            const filteredDates = selectedEvent.eventDates.filter(date => 
              assignedDateIds.includes(date.id)
            );
            
            console.log(`Filtered dates count for ${userData.userType}:`, filteredDates.length);
            setFilteredDates(filteredDates);
            setIsLoadingDates(false);
            filteringInProgressRef.current = false;
            return;
          } else if (selectedEvent.type === 'show' && selectedEvent.eventDates.length === 1) {
            // Special case: for shows with one date, if the user has the event in their list, they have access
            console.log("Show with single date - checking if user has general access");
            setFilteredDates(selectedEvent.eventDates);
            setIsLoadingDates(false);
            filteringInProgressRef.current = false;
            return;
          } else {
            console.log(`${userData.userType} not assigned to any dates`);
            setFilteredDates([]);
            setIsLoadingDates(false);
            filteringInProgressRef.current = false;
            return;
          }
        }
      } else {
        // User doesn't have this event in their list
        console.log("User not assigned to this event, showing no dates");
        setFilteredDates([]);
        setIsLoadingDates(false);
        filteringInProgressRef.current = false;
        return;
      }
    } catch (error) {
      console.error("Error filtering dates:", error);
      setFilteredDates([]);
      setIsLoadingDates(false);
      filteringInProgressRef.current = false;
    }
  }, [selectedEvent, userData, events]);

  const selectedDateRef = useRef(null);
  
  useEffect(() => {
    if (selectedDate && selectedDateRef.current) {
      selectedDateRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedDate]);

  const formatDate = (dateString) => {
    // Ensure we have a valid date
    if (!dateString) return '';
    
    try {
      // Normalize the date to YYYY-MM-DD format first
      const normalizedDate = normalizeDate(dateString);
      
      // Parse the normalized date
      const date = new Date(normalizedDate);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string after normalization:', dateString, normalizedDate);
        return 'Invalid date';
      }
      
      // Format using toLocaleDateString
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  const handleDateClick = (date) => {
    console.log("Date clicked:", date.id);
    setSelectedDate(date.id);
    
    // Use consistent navigation pattern for both tours and shows
    if (selectedEvent.type === 'tour') {
      if (userData.userType === 'Vendor' || userData.userType === 'Staff') {
        navigate(`/tour/${selectedEvent.id}/${date.id}`, { 
          state: { activeTab: 'event' } 
        });
      } else {
        navigate(`/tour/${selectedEvent.id}/${date.id}`);
      }
    } else {
      // For shows, also include the date ID in navigation for consistency
      navigate(`/show/${selectedEvent.id}/${date.id}`, {
        state: { activeTab: userData.userType === 'Vendor' || userData.userType === 'Staff' ? 'event' : 'schedule' }
      });
    }
  };

  const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, eventName, eventType, isProcessing }) => {
    if (!isOpen) return null;
  
    return (
      <div className={styles['modal-overlay']}>
        <div className={styles['modal-content']}>
          <h2 className={styles['modal-title']}>Delete {eventType === 'tour' ? 'Tour' : 'Show'}</h2>
          <p className={styles['modal-message']}>
            Are you sure you want to delete the {eventType} "{eventName}"? This will remove all data including schedules, files, and tickets for all members. This action cannot be undone.
          </p>
          <div className={styles['modal-buttons']}>
            <button 
              className={`${styles['modal-button']} ${styles['cancel-button']}`}
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button 
              className={`${styles['modal-button']} ${styles['delete-button']}`}
              onClick={onConfirm}
              disabled={isProcessing}
            >
              {isProcessing ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleSelectEvent = (event) => {
    console.log("Event selected:", event.id, event.type);
    
    // Skip if this is the same event already selected
    if (selectedEvent?.id === event.id && selectedEvent?.type === event.type) {
      console.log("Event already selected, skipping");
      setIsDropdownOpen(false);
      return;
    }
    
    // Update the selected event first
    setSelectedEvent(event);
    
    // Close the dropdown
    setIsDropdownOpen(false);
  };

  const handleDeleteEvent = async (event) => {
    // First check if the current user is the creator
    if (event.createdBy?.userId !== userData.userId) {
      alert(`Only the creator of this ${event.type} can delete it.`);
      return;
    }
    
    // Set the event to delete and show the confirmation modal
    setEventToDelete(event);
    setShowDeleteModal(true);
  };
  
  // Add a confirmDeleteEvent function
  const confirmDeleteEvent = async () => {
    if (!eventToDelete) return;
    
    setIsDeleting(true);
    try {
      console.log("Deleting event:", eventToDelete.id, eventToDelete.type);
      // Use the centralized deleteEvent function from TourContext
      const success = await deleteEvent(eventToDelete.id, eventToDelete.type, userData?.userId);
      
      if (success) {
        console.log("Event deleted successfully");
        
        // If we deleted the currently selected event, navigate back to profile
        if (selectedEvent?.id === eventToDelete.id && selectedEvent?.type === eventToDelete.type) {
          console.log("Deleted the currently selected event, navigating back to profile");
          navigate(`/${userData.userName}`);
        }
      } else {
        alert(`Failed to delete ${eventToDelete.type}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error deleting ${eventToDelete.type}:`, error);
      alert(`Failed to delete ${eventToDelete.type}. Please try again.`);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setEventToDelete(null);
    }
  };

  const checkEventLimit = async () => {
    if (!userData?.userId) return false;
    if (!userData?.membershipTier) return false;
    
    try {
      const db = getFirestoreInstance();
      
      // Count documents in the assignedEvents subcollection
      const eventsQuery = query(collection(db, 'users', userData.userId, 'assignedEvents'));
      const snapshot = await getCountFromServer(eventsQuery);
      const currentEventCount = snapshot.data().count;
      
      switch (userData.membershipTier) {
        case 'Free':
          return currentEventCount < 1;
        case 'Basic':
          return currentEventCount < 10;
        case 'Pro':
          return currentEventCount < 30;
        case 'Elite':
          return true;
        default:
          return false;
      }
    } catch (error) {
      console.error('Error checking event limit:', error);
      return false;
    }
  };

  const handleCreateEvent = (eventType) => {
    console.log(`Create ${eventType} button clicked`);
    // Check membership tier and event limits
    const { membershipTier } = userData;
    const currentEventCount = events ? events.length : 0;

    let canCreateEvent = true;
    let message = '';

    switch (membershipTier) {
      case 'Free':
        if (currentEventCount >= 1) {
          canCreateEvent = false;
          message = 'Free membership allows only 1 event. Please upgrade to create more events.';
        }
        break;
      case 'Basic':
        if (currentEventCount >= 10) {
          canCreateEvent = false;
          message = 'Basic membership allows up to 10 events. Please upgrade to create more events.';
        }
        break;
      case 'Pro':
        if (currentEventCount >= 15) {
          canCreateEvent = false;
          message = 'Pro membership allows up to 15 events. Please upgrade to create more events.';
        }
        break;
      case 'Elite':
        // No limit for Elite members
        break;
      default:
        canCreateEvent = false;
        message = 'Unable to determine event limits. Please check your membership status.';
    }

    if (!canCreateEvent) {
      alert(message);
      return;
    }

    navigate('/create-tour', { state: { eventType } });
    setIsDropdownOpen(false);
  };

  // Function to determine whether to show the Create Event button
  const shouldShowCreateEventButton = () => {
    if (!userData || !userData.userType) return false;
    
    // Show for Artist, Manager, and Organization
    const allowedTypes = ['Artist', 'Manager', 'Organization'];
    return allowedTypes.includes(userData.userType);
  };

  // Sort events by creation date (newest first) - with safety checks
  const sortedEvents = events && events.length ? [...events].sort((a, b) => {
    // First check for createdAt timestamp
    if (a?.createdAt && b?.createdAt) {
      const aDate = a.createdAt.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt);
      const bDate = b.createdAt.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt);
      return bDate - aDate; // Descending (newest first)
    }
    
    // Fallback to dates if available
    if (a?.eventDates?.length && b?.eventDates?.length) {
      const aFirstDate = new Date(a.eventDates[0].date);
      const bFirstDate = new Date(b.eventDates[0].date);
      return bFirstDate - aFirstDate; // Descending (newest first)
    }
    
    // Default to 0 (no change)
    return 0;
  }) : [];

  // Filter events by type (tour, show, all) - with safety check
  const filteredEvents = sortedEvents && sortedEvents.length ? sortedEvents.filter(event => 
    filterType === 'all' || (event && event.type === filterType)
  ) : [];

  console.log("Rendering RightMenu with:");
  console.log("- selectedEvent:", selectedEvent?.id, selectedEvent?.type);
  console.log("- filteredDates count:", filteredDates?.length || 0);
  console.log("- isDropdownOpen:", isDropdownOpen);
  console.log("- events count:", events?.length || 0);
  console.log("- filtered events count:", filteredEvents?.length || 0);

  // If user is not logged in, return empty div with the class for styling
  if (!userData) {
    return <div className={styles['right-menu']}></div>;
  }

  // Helper function to get event name
  const getEventName = (event) => {
    if (event.type === 'tour') {
      return event.tourName || 'Unnamed Tour';
    } else {
      return event.showName || event.name || 'Unnamed Show';
    }
  };

  // Helper function to get event icon
  const getEventIcon = (eventType) => {
    return eventType === 'tour' ? faCalendar : faMusic;
  };

  return (
    <div className={`${styles['right-menu']} ${isDropdownOpen ? styles['dropdown-open'] : ''}`}>
      <div className={styles['tour-selector']}>
        <button 
          className={styles['tour-selector-button']}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <div className={styles['tour-info']}>
            {!events || events.length === 0 ? (
              <span className={styles['tour-act-name']}>No events yet</span>
            ) : (
              <>
                <span className={styles['tour-act-name']}>
                  {selectedEvent ? 
                    (selectedEvent.actName || (selectedEvent.type === 'tour' ? 'Select Tour' : 'Select Show')) : 
                    'Select Event'}
                </span>
                <span className={styles['tour-name']}>
                  {selectedEvent ? getEventName(selectedEvent) : ''}
                </span>
              </>
            )}
          </div>
          <FontAwesomeIcon icon={isDropdownOpen ? faChevronUp : faChevronDown} />
        </button>
        
        {isDropdownOpen && (
          <div className={styles['tour-dropdown']}>
            {/* Filter tabs */}
            <div className={styles['filter-tabs']}>
              <button 
                className={`${styles['filter-tab']} ${filterType === 'all' ? styles['active'] : ''}`}
                onClick={() => setFilterType('all')}
              >
                All
              </button>
              <button 
                className={`${styles['filter-tab']} ${filterType === 'tour' ? styles['active'] : ''}`}
                onClick={() => setFilterType('tour')}
              >
                Tours
              </button>
              <button 
                className={`${styles['filter-tab']} ${filterType === 'show' ? styles['active'] : ''}`}
                onClick={() => setFilterType('show')}
              >
                Shows
              </button>
            </div>

            {filteredEvents.length === 0 ? (
              <div className={styles['no-tours-message']}>
                <p>You don't have any {filterType === 'all' ? 'events' : filterType === 'tour' ? 'tours' : 'shows'} yet.</p>
                {shouldShowCreateEventButton() && (
                  <div className={styles['create-buttons']}>
                    <button 
                      className={styles['create-tour-button']}
                      onClick={() => handleCreateEvent('tour')}
                    >
                      <FontAwesomeIcon icon={faCalendar} />
                      <span>Create Tour</span>
                    </button>
                    <button 
                      className={styles['create-show-button']}
                      onClick={() => handleCreateEvent('show')}
                    >
                      <FontAwesomeIcon icon={faMusic} />
                      <span>Add Show</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {filteredEvents && filteredEvents.length > 0 ? (
                  <>
                    {filteredEvents.map(event => (
                      <div key={`${event.type}_${event.id}`} className={styles['tour-dropdown-item-container']}>
                        <button
                          className={`${styles['tour-dropdown-item']} ${
                            selectedEvent?.id === event.id && selectedEvent?.type === event.type ? styles.selected : ''
                          }`}
                          onClick={() => handleSelectEvent(event)}
                        >
                          <FontAwesomeIcon 
                            icon={getEventIcon(event.type)} 
                            className={styles['event-type-icon']} 
                          />
                          <div className={styles['event-info']}>
                            <span className={styles['tour-act-name']}>
                              {event.actName || (event.type === 'tour' ? 'Tour' : 'Show')}
                            </span>
                            <span className={styles['tour-name']}>
                              {getEventName(event)}
                            </span>
                          </div>
                        </button>
                        <div className={styles['tour-actions']}>
                          <button 
                            className={styles['action-button']}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/create-tour', { 
                                state: { 
                                  tourData: event,
                                  eventType: event.type 
                                } 
                              });
                            }}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          {/* Only show delete button if user is the creator */}
                          {event.createdBy?.userId === userData.userId && (
                            <button 
                              className={styles['action-button']}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEvent(event);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className={styles['no-tours-message']}>
                    <p>No {filterType === 'all' ? 'events' : filterType === 'tour' ? 'tours' : 'shows'} found.</p>
                  </div>
                )}
                
              {/* Load More Button */}
                {hasMoreEvents && (
                  <button 
                    className={styles['load-more-button']}
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faArrowDown} />
                        <span>Load More</span>
                      </>
                    )}
                  </button>
                )}
                
                {/* Only show Create Event buttons for certain user types */}
                {shouldShowCreateEventButton() && (
                  <div className={styles['create-buttons']}>
                    <button 
                      className={styles['create-tour-button']}
                      onClick={() => handleCreateEvent('tour')}
                      disabled={
                        (userData.membershipTier === 'Free' && events && events.length >= 1) ||
                        (userData.membershipTier === 'Basic' && events && events.length >= 10)
                      }
                    >
                      <FontAwesomeIcon icon={faCalendar} />
                      <span>Create Tour</span>
                    </button>
                    <button 
                      className={styles['create-show-button']}
                      onClick={() => handleCreateEvent('show')}
                      disabled={
                        (userData.membershipTier === 'Free' && events && events.length >= 1) ||
                        (userData.membershipTier === 'Basic' && events && events.length >= 10)
                      }
                    >
                      <FontAwesomeIcon icon={faMusic} />
                      <span>Add Show</span>
                    </button>
                  </div>
                )}
                </>
            )}
          </div>
        )}

        {showDeleteModal && (
          <DeleteConfirmationModal 
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDeleteEvent}
            eventName={eventToDelete?.tourName || eventToDelete?.name || 'this event'}
            eventType={eventToDelete?.type || 'event'}
            isProcessing={isDeleting}
          />
        )}
      </div>

      {selectedEvent && events && events.length > 0 && (
        <div className={styles['tour-dates-section']}>
          {isLoadingDates ? (
            <div className={styles['loading-dates']}>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading dates...</span>
            </div>
          ) : filteredDates && filteredDates.length > 0 ? (
            <div className={styles['dates-list']}>
            {filteredDates
  .sort((a, b) => new Date(normalizeDate(a.date)) - new Date(normalizeDate(b.date)))
  .map((date) => {
    const isRescheduled = date.rescheduleDate?.isRescheduled;
    // Normalize dates for both original and rescheduled dates
    const rescheduledDate = isRescheduled ? new Date(normalizeDate(date.rescheduleDate.newDate)) : null;
    const isCancelled = date.cancelled;
    
    // Display rescheduled date if available, otherwise show original date
    const displayDate = isRescheduled ? rescheduledDate : new Date(normalizeDate(date.date));

    return (
      <div 
        key={date.id}
        ref={selectedDate === date.id ? selectedDateRef : null}
        className={`
          ${styles['date-card']} 
          ${selectedDate === date.id ? styles.selected : ''}
          ${isCancelled ? styles['cancelled'] : ''}
          ${isRescheduled ? styles['rescheduled'] : ''}
        `}
        onClick={() => handleDateClick(date)}
      >
        <div className={styles['date-card-header']}>
          <span className={styles.date}>{formatDate(displayDate)}</span>
          <div className={styles['status-badges']}>
            {isCancelled && <span className={styles['cancelled-badge']}>Cancelled</span>}
            {isRescheduled && <span className={styles['rescheduled-badge']}>Rescheduled</span>}
          </div>
        </div>
        {/* Check multiple possible paths for venue details */}
        {(date.event?.venueDetails?.venueData || 
          date.venueDetails?.venueData) && (
          <div className={styles['date-card-content']}>
            <div className={styles['venue-info']}>
              <div className={styles['venue-details']}>
                <span>
                  {date.event?.venueDetails?.venueData?.displayName || 
                   date.venueDetails?.venueData?.displayName || 
                   'Venue'}
                </span>
                <span className={styles['date-location']}>
                  {date.event?.venueDetails?.venueData?.formattedAddress || 
                   date.venueDetails?.venueData?.formattedAddress || 
                   'Location not available'}
                </span>
                {isRescheduled && (
                  <span className={styles['rescheduled-info']}>
                    Rescheduled from: {formatDate(new Date(normalizeDate(date.date)))}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  })}
              </div>
            ) : (
              <div className={styles['no-dates-message']}>
                {userData.userType === 'Manager' || userData.userType === 'Organization' ? 
                  `You don't have access to any dates for this ${selectedEvent.type}.` : 
                  `No dates available for this ${selectedEvent.type}.`}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default RightMenu;